import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useLayoutEffect, useState } from 'react';

import { ApplicationErrorModal } from 'components/modal/ApplicationErrorModal';
import { useApiContext } from 'context/ApiContext';
import { Store, useQueryUser, User } from 'hooks/application/useQueryUser';

export type FormattedStore = Pick<Store, 'idLoja' | 'dsNomeFantasia' | 'cdLoja'> | null;

export type ApplicationContext = {
    store: Store;
    setStore: Dispatch<SetStateAction<Store | null>>;
    formattedStore: FormattedStore;
    user: User;
};

const ApplicationContext = createContext<ApplicationContext>({} as ApplicationContext);

export function ApplicationProvider({ children }: { children: ReactNode }) {
    const { setApiHeader } = useApiContext();
    const { data: user, isFetching: isFetchingUser, isError: isErrorUser } = useQueryUser();
    const [store, setStore] = useState<Store | null>(null);

    /**
     * Define a loja padrão no state.
     */
    useLayoutEffect(() => {
        if (user) setStore(user.loja);
    }, [user]);

    /**
     * Sincroniza os headers dinâmicos da API quando store ou user mudam.
     */
    useLayoutEffect(() => {
        if (store) setApiHeader('X-idEmpresa', store.idLoja);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store]);

    // enquanto não possui os dados gerais, não renderiza nada a não ser o loader da aplicação.
    if (isFetchingUser) return null;

    if (isErrorUser || !user || !store) return <ApplicationErrorModal type="user" />;

    return (
        <ApplicationContext.Provider
            value={{
                user: user,
                store: store,
                setStore,

                formattedStore: {
                    idLoja: store.idLoja,
                    cdLoja: store.cdLoja,
                    dsNomeFantasia: store.dsNomeFantasia,
                },
            }}
        >
            {children}
        </ApplicationContext.Provider>
    );
}

export const useApplicationContext = (): ApplicationContext => useContext(ApplicationContext);
